const {
  ALL_BROKER_CONNECTION_SOURCE,
  buildBrokerConnectPath,
} = require('./brokerConnectionSource');
const { SEO_EXPANSION_VERIFIED_DATE, buildSeoExpansionContent } = require('./seoExpansionContent');

const SOURCE_VERIFIED_DATE = '2026-08-17';
const { BOTSPOT_EDITORIAL_AUTHOR } = require('./editorialContent');
const { withGuideWorkflowExample } = require('./guideWorkflowExamples');

const agentProofPoints = [
  {
    title: 'Research by conversation',
    text: 'Ask the agent to investigate markets, companies, filings, strategies, and your connected account context.',
  },
  {
    title: 'Place approved direct trades',
    text: 'When direct trading is enabled, request a one-time trade in plain English, inspect the order, and approve it before submission.',
  },
  {
    title: 'Build complete algorithms',
    text: 'Create, revise, backtest, connect, and operate Lumibot strategies without assembling the full application stack yourself.',
  },
  {
    title: 'Use the AI client you prefer',
    text: 'Work in BotSpot or connect through ChatGPT, Claude, Cursor, Codex, and other compatible MCP clients.',
  },
];

const { comparisonPages: seoComparisonPages, guidePages: seoGuidePages } = buildSeoExpansionContent(
  { agentProofPoints },
);

const comparisonPages = [
  {
    path: '/compare',
    title: 'AI Trading Platform Comparisons | BotSpot',
    description:
      'See how BotSpot combines conversational research, approved direct trading, strategy building, backtesting, and broker connections in one AI trading workspace.',
    pageType: 'article',
    lastmod: SOURCE_VERIFIED_DATE,
    content: {
      eyebrow: 'BotSpot AI trading workspace',
      heading: 'Trade, research, and automate with one AI agent',
      summary:
        'BotSpot does more than generate trading algorithms. Research an idea, ask for an approved one-time trade, build and backtest a complete strategy, connect a supported broker, and operate the workflow from BotSpot or the AI client you already use.',
      primaryAction: { label: 'Start with BotSpot', url: '/sales?showLogin=1' },
      secondaryAction: { label: 'See MCP setup', url: '/agents' },
      proofPoints: agentProofPoints,
      sections: [
        {
          heading: 'Compare the full job, not one feature',
          paragraphs: [
            'A code-first quant platform, a broker API, and an AI trading workspace solve different jobs. BotSpot combines a conversational agent with research tools, approved trading actions, strategy code, backtests, supported broker connections, and ongoing operations.',
          ],
          bullets: [
            'Compare BotSpot with QuantConnect when deciding between an agent-led workspace and a code-first quantitative platform.',
            'Use BotSpot with a supported broker when you want the broker to hold and execute while BotSpot supplies the AI workflow.',
            'Review the current broker pages for supported assets, connection modes, authentication methods, and broker-specific warnings.',
          ],
        },
      ],
      cards: [
        {
          eyebrow: 'Platform comparison',
          heading: 'BotSpot vs QuantConnect',
          summary:
            'Compare a conversational AI trading workspace with a code-first quantitative research and execution platform.',
          url: '/compare/botspot-vs-quantconnect',
        },
        {
          eyebrow: 'Broker integration',
          heading: 'Use BotSpot with Alpaca',
          summary:
            'Let Alpaca provide the connected brokerage account while BotSpot supplies research, approved trades, and strategy operations.',
          url: '/compare/botspot-vs-alpaca',
        },
        {
          eyebrow: 'Brokerage platform comparison',
          heading: 'BotSpot vs Interactive Brokers',
          summary:
            'Compare BotSpot’s AI workflow with Interactive Brokers brokerage accounts, trading platforms, and APIs.',
          url: '/compare/botspot-vs-interactive-brokers',
        },
        ...seoComparisonPages.map((route) => ({
          eyebrow: route.content.eyebrow,
          heading: route.content.heading,
          summary: route.description,
          url: route.path,
        })),
        {
          eyebrow: 'Broker connections',
          heading: 'Explore every supported broker',
          summary:
            'See current BotSpot connection pages for stocks, options, futures, and crypto brokers.',
          url: '/brokers',
        },
        {
          eyebrow: 'Buying guide',
          heading: 'Choose an automated trading platform',
          summary:
            'Decide whether you need an AI workspace, quantitative platform, broker API, or open-source framework.',
          url: '/guides/automated-trading-platforms',
        },
      ],
      relatedLinks: [
        { label: 'MCP for Agents', url: '/agents' },
        { label: 'View supported brokers', url: '/brokers' },
        { label: 'Review BotSpot pricing', url: '/pricing' },
      ],
      sources: [
        { label: 'BotSpot MCP for Agents', url: '/agents' },
        { label: 'BotSpot broker connection manifest', url: '/.well-known/botspot-manifest.json' },
        { label: 'Lumibot documentation', url: 'https://lumibot.lumiwealth.com/' },
      ],
      verifiedAt: SOURCE_VERIFIED_DATE,
    },
  },
  {
    path: '/compare/botspot-vs-quantconnect',
    title: 'BotSpot vs QuantConnect | AI Trading Platform Comparison',
    description:
      'Compare BotSpot and QuantConnect for conversational research, approved direct trades, algorithm building, backtesting, infrastructure, and broker workflows.',
    pageType: 'article',
    lastmod: SOURCE_VERIFIED_DATE,
    content: {
      eyebrow: 'AI agent vs code-first quant platform',
      heading: 'BotSpot vs QuantConnect: trade by conversation or build algorithms with AI',
      summary:
        'BotSpot starts with an AI agent that can research, prepare an approved one-time trade when direct trading is enabled, or build and operate a complete algorithm. QuantConnect starts with code and a quantitative research and execution environment.',
      primaryAction: { label: 'Start with BotSpot', url: '/sales?showLogin=1' },
      secondaryAction: { label: 'See MCP setup', url: '/agents' },
      proofPoints: agentProofPoints,
      comparisonRows: [
        {
          capability: 'Starting point',
          botspot: 'Ask in plain English, then inspect the agent work and resulting artifacts.',
          alternative: 'Write an algorithm against the LEAN engine and QuantConnect platform.',
        },
        {
          capability: 'Direct trading',
          botspot: 'Request an approved one-time trade through a supported connected broker.',
          alternative:
            'Deploy and run live trading algorithms through the documented platform workflow.',
        },
        {
          capability: 'Research',
          botspot:
            'Use conversational research across market data, public sources, and available account context.',
          alternative: 'Use datasets, research notebooks, APIs, and code.',
        },
        {
          capability: 'Where you work',
          botspot: 'Use BotSpot, ChatGPT, Claude, Cursor, Codex, or another compatible MCP client.',
          alternative: 'Use QuantConnect interfaces, APIs, and the LEAN development model.',
        },
        {
          capability: 'Strategy lifecycle',
          botspot:
            'Build, revise, backtest, connect, and operate a Lumibot strategy in one managed product.',
          alternative:
            'Control algorithm code, research, backtests, optimization, and live deployment in a quantitative platform.',
        },
      ],
      sections: [
        {
          heading: 'What BotSpot can do that changes the comparison',
          paragraphs: [
            'You do not need to begin by deciding to build an algorithm. Ask the BotSpot agent to research a question, explain what it found, prepare a one-time broker order for your approval, or turn the idea into a testable automated strategy.',
            'The same BotSpot capabilities can be reached through ChatGPT, Claude, Cursor, Codex, and other compatible MCP clients. That makes BotSpot an AI trading workspace, not only a strategy generator.',
          ],
        },
        {
          heading: 'When QuantConnect may fit better',
          bullets: [
            'Your quantitative engineering team wants a code-first environment and direct control over the LEAN engine.',
            'Your workflow is centered on datasets, research notebooks, optimization, and algorithm infrastructure.',
            'Your team is prepared to own more of the engineering and operational workflow.',
          ],
        },
        {
          heading: 'Who should use which',
          paragraphs: [
            'Use BotSpot if you want an AI agent that can research, prepare an approved one-time trade, and generate inspectable Lumibot code without first standing up a quantitative platform.',
            'Use QuantConnect if your team already writes algorithms against LEAN and wants notebooks, datasets, and infrastructure as the starting point.',
            'Backtests on both products are historical simulations. They are not live results and do not guarantee future performance. Read drawdown and date range whenever a number is shown.',
          ],
        },
        {
          heading: 'Risk and limitations',
          paragraphs: [
            'Neither platform removes market risk. BotSpot requires explicit approval for one-time trade actions, and automated strategies require review before deployment. Backtests are historical simulations and do not guarantee future results.',
          ],
        },
      ],
      faq: [
        {
          question: 'Can BotSpot trade without first building an algorithm?',
          answer:
            'When direct trading is enabled for the session, BotSpot supports approved one-time trading actions through supported connected brokers. The user reviews and approves the order before submission. Broker, account, asset, and product eligibility still apply.',
        },
        {
          question: 'Can BotSpot also build complete algorithms?',
          answer:
            'Yes. The agent can create and revise Lumibot strategy code, run backtests, and help operate the strategy workflow. Users remain responsible for reviewing logic, assumptions, and risk.',
        },
      ],
      relatedLinks: [
        { label: 'MCP for Agents', url: '/agents' },
        { label: 'View supported brokers', url: '/brokers' },
        { label: 'Review BotSpot pricing', url: '/pricing' },
      ],
      sources: [
        { label: 'BotSpot MCP for Agents', url: '/agents' },
        {
          label: 'QuantConnect algorithm documentation',
          url: 'https://www.quantconnect.com/docs/v2/writing-algorithms',
        },
        { label: 'LEAN engine repository', url: 'https://github.com/QuantConnect/Lean' },
        { label: 'Lumibot documentation', url: 'https://lumibot.lumiwealth.com/' },
      ],
      verifiedAt: SOURCE_VERIFIED_DATE,
    },
  },
  ...seoComparisonPages,
  {
    path: '/compare/botspot-vs-alpaca',
    title: 'Use BotSpot with Alpaca | AI Trading and Broker Integration',
    description:
      'Connect Alpaca to BotSpot for conversational research, approved one-time trades, AI strategy building, backtesting, and automated operations.',
    pageType: 'article',
    lastmod: SOURCE_VERIFIED_DATE,
    content: {
      eyebrow: 'AI workspace plus connected broker',
      heading: 'Use BotSpot with Alpaca',
      summary:
        'BotSpot supplies the AI trading workspace. Alpaca supplies the connected brokerage account and execution layer. Together, you can research, request an approved one-time trade when direct trading is enabled, or build and operate a complete automated strategy.',
      primaryAction: {
        label: 'Connect Alpaca to BotSpot',
        url: buildBrokerConnectPath('alpaca'),
      },
      secondaryAction: { label: 'Explore all broker connections', url: '/brokers' },
      proofPoints: agentProofPoints,
      comparisonRows: [
        {
          capability: 'AI workspace',
          botspot: 'Conversational research, trading actions, and strategy lifecycle.',
          alternative: 'Alpaca provides brokerage and developer APIs.',
        },
        {
          capability: 'Account and execution',
          botspot: 'Uses your supported connected broker account.',
          alternative: 'Alpaca holds the brokerage account and executes eligible orders.',
        },
        {
          capability: 'Application ownership',
          botspot:
            'Provides the product, agent, revisions, backtests, approvals, and operations workflow.',
          alternative: 'Direct API users build and maintain their own application layer.',
        },
      ],
      sections: [
        {
          heading: 'Why this is an integration, not a winner-take-all comparison',
          paragraphs: [
            'Alpaca and BotSpot solve different parts of the workflow. Connect Alpaca when you want its brokerage account and API execution with BotSpot handling the conversational product layer around research, approved trades, and automation.',
          ],
          bullets: [
            'Ask the agent to research an idea before deciding whether to trade or automate it.',
            'Prepare a one-time order, inspect the details, and approve it before BotSpot submits it.',
            'Build, revise, backtest, and operate a Lumibot algorithm using the same connected account workflow.',
          ],
        },
        {
          heading: 'Who should use which',
          paragraphs: [
            'Use BotSpot with Alpaca when you want the AI workspace for research, approved one-time trades, and Lumibot strategy operations while Alpaca holds the account.',
            'Use Alpaca APIs directly when you want to build your own application without BotSpot.',
            'These products stack rather than replace each other. Simulated BotSpot backtests are not Alpaca live results. Include drawdown and date range whenever a number is shown.',
          ],
        },
        {
          heading: 'Connection details',
          paragraphs: [
            'BotSpot currently lists Alpaca for stocks, options, and crypto with paper and live connection modes. OAuth and API-key connection methods are available. Actual products, permissions, data, fees, and order eligibility depend on the Alpaca account and current broker policies.',
          ],
        },
      ],
      faq: [
        {
          question: 'Can BotSpot connect to Alpaca?',
          answer:
            'Yes. Alpaca is listed in BotSpot as a supported connection for paper and live workflows. Available products and permissions depend on your Alpaca account.',
        },
        {
          question: 'Do I need to build an algorithm to trade through BotSpot and Alpaca?',
          answer:
            'No. When direct trading is enabled, BotSpot can prepare an approved one-time trade for a supported connected account. You can also use the agent to build and backtest a complete automated strategy.',
        },
      ],
      relatedLinks: [
        { label: 'View the Alpaca broker page', url: '/brokers/alpaca' },
        { label: 'MCP for Agents', url: '/agents' },
        { label: 'View every supported broker', url: '/brokers' },
      ],
      sources: [
        { label: 'BotSpot MCP for Agents', url: '/agents' },
        { label: 'BotSpot broker connection manifest', url: '/.well-known/botspot-manifest.json' },
        { label: 'Alpaca documentation', url: 'https://docs.alpaca.markets/us/docs/trading-api' },
      ],
      verifiedAt: SOURCE_VERIFIED_DATE,
    },
  },
  {
    path: '/compare/botspot-vs-interactive-brokers',
    title: 'BotSpot vs Interactive Brokers | Trading Platform Comparison',
    description:
      'Compare BotSpot and Interactive Brokers by AI workflow, brokerage access, APIs, automation, and current connection availability.',
    pageType: 'article',
    lastmod: SOURCE_VERIFIED_DATE,
    content: {
      eyebrow: 'AI workspace vs brokerage platform',
      heading: 'BotSpot vs Interactive Brokers',
      summary:
        'BotSpot supplies a conversational research, trading, and strategy workflow. Interactive Brokers supplies brokerage accounts, trading platforms, and APIs. The current BotSpot broker source does not list Interactive Brokers as an available connection.',
      primaryAction: { label: 'View supported BotSpot brokers', url: '/brokers' },
      secondaryAction: { label: 'See MCP setup', url: '/agents' },
      proofPoints: agentProofPoints,
      comparisonRows: [
        {
          capability: 'Starting point',
          botspot:
            'Ask an agent to research, prepare an approved trade, or build inspectable strategy code.',
          alternative:
            'Open an Interactive Brokers account and use TWS, Client Portal, or IBKR APIs.',
        },
        {
          capability: 'Custody and execution',
          botspot: 'Uses a supported connected broker. BotSpot does not hold customer funds.',
          alternative:
            'Interactive Brokers holds the brokerage account and executes eligible orders.',
        },
        {
          capability: 'Strategy automation',
          botspot: 'Build, backtest, and operate Lumibot strategies in a managed workspace.',
          alternative:
            'Build custom applications against IBKR APIs or use IBKR desktop and web platforms.',
        },
        {
          capability: 'Current BotSpot connection',
          botspot:
            'Not listed as an available standard connection in the public BotSpot broker manifest.',
          alternative: 'Direct brokerage access does not require BotSpot.',
        },
      ],
      sections: [
        {
          heading: 'The practical difference',
          paragraphs: [
            'BotSpot focuses on the work around a trade or strategy: research, user-approved actions, code, simulated backtests, revisions, and operations. Interactive Brokers focuses on the brokerage account, execution, trading interfaces, and developer APIs.',
            'A simulated BotSpot backtest is not an IBKR live track record. If you compare numbers, include drawdown and the simulation date range, and treat the result as hypothetical.',
          ],
        },
        {
          heading: 'Connection status matters',
          paragraphs: [
            'The current BotSpot public broker manifest does not list Interactive Brokers as an available standard connection. This page compares product roles and does not imply an integration. Check the broker list before assuming a BotSpot bot can place IBKR orders.',
          ],
        },
        {
          heading: 'Who should use which',
          paragraphs: [
            'Use BotSpot if you want an AI workspace for research, approved one-time trades, and automated Lumibot strategies through a currently supported broker.',
            'Use Interactive Brokers if you need IBKR account features, TWS or Client Portal, or you are building directly on IBKR APIs.',
            'Use both only if you later connect through a supported path. Today this comparison does not claim that BotSpot can drive an IBKR account.',
          ],
        },
      ],
      faq: [
        {
          question: 'Is Interactive Brokers currently listed as a BotSpot broker connection?',
          answer:
            'No. The current BotSpot public broker manifest does not list Interactive Brokers as an available standard connection. Check the manifest for the current supported set.',
        },
      ],
      relatedLinks: [
        { label: 'View supported BotSpot brokers', url: '/brokers' },
        { label: 'MCP for Agents', url: '/agents' },
      ],
      sources: [
        { label: 'BotSpot broker connection manifest', url: '/.well-known/botspot-manifest.json' },
        {
          label: 'Interactive Brokers API overview',
          url: 'https://www.interactivebrokers.com/campus/ibkr-api-page/',
        },
      ],
      verifiedAt: SOURCE_VERIFIED_DATE,
    },
  },
  {
    path: '/guides/automated-trading-platforms',
    title: 'How to Choose an Automated Trading Platform | BotSpot',
    description:
      'Choose among an AI trading workspace, quantitative platform, broker API, or open-source framework by comparing the complete workflow.',
    pageType: 'article',
    lastmod: SOURCE_VERIFIED_DATE,
    content: {
      eyebrow: 'Automated trading buyer guide',
      heading: 'Choose the workflow, not just the backtest tool',
      summary:
        'Start by deciding whether you need conversational research and trading, a complete AI strategy lifecycle, quantitative infrastructure, a broker API, or an open-source framework.',
      primaryAction: { label: 'Start with BotSpot', url: '/sales?showLogin=1' },
      secondaryAction: {
        label: 'Compare BotSpot and QuantConnect',
        url: '/compare/botspot-vs-quantconnect',
      },
      proofPoints: agentProofPoints,
      sections: [
        {
          heading: '1. Decide what you want to do first',
          bullets: [
            'Research or trade conversationally: choose an AI agent with the data and approval tools you need.',
            'Build and operate algorithms: compare code generation, revisions, backtests, broker connections, and monitoring.',
            'Own quantitative infrastructure: compare engines, datasets, notebooks, optimization, and deployment controls.',
            'Build your own application: compare broker APIs, market access, authentication, and operational obligations.',
          ],
        },
        {
          heading: '2. Ask who owns production operations',
          paragraphs: [
            'The largest difference is often who owns credentials, market data, scheduling, monitoring, retries, deployment safety, and incident response after a strategy moves beyond a backtest.',
          ],
        },
        {
          heading: '3. Demand evidence you can inspect',
          bullets: [
            'Read official documentation and current integration lists.',
            'Inspect strategy code and assumptions before trusting a backtest. Treat backtests as simulations and include drawdown plus date range whenever you show a number.',
            'Distinguish simulated results from live history.',
            'Verify fees, data coverage, broker permissions, approval boundaries, and operational limits.',
          ],
        },
        {
          heading: 'Risk and suitability',
          paragraphs: [
            'Automated trading can lose money. Historical backtests do not guarantee future performance. Platform tooling does not replace strategy review, risk controls, broker due diligence, tax advice, legal advice, or investment advice.',
          ],
        },
      ],
      relatedLinks: [
        { label: 'Compare BotSpot and QuantConnect', url: '/compare/botspot-vs-quantconnect' },
        { label: 'Use BotSpot with Alpaca', url: '/compare/botspot-vs-alpaca' },
        { label: 'Explore supported brokers', url: '/brokers' },
        { label: 'MCP for Agents', url: '/agents' },
      ],
      sources: [
        { label: 'BotSpot public manifest', url: '/.well-known/botspot-manifest.json' },
        { label: 'Lumibot documentation', url: 'https://lumibot.lumiwealth.com/' },
        {
          label: 'QuantConnect algorithm documentation',
          url: 'https://www.quantconnect.com/docs/v2/writing-algorithms',
        },
      ],
      verifiedAt: SOURCE_VERIFIED_DATE,
    },
  },
  ...seoGuidePages,
];

const guideDetailPages = comparisonPages.filter((route) => route.path.startsWith('/guides/'));

const guideHubPage = {
  path: '/guides',
  title: 'AI Trading and Backtesting Guides | BotSpot',
  description:
    'Explore practical BotSpot guides for building AI trading strategies, backtesting evidence, controlling risk, connecting brokers, and using MCP clients.',
  pageType: 'website',
  lastmod: SEO_EXPANSION_VERIFIED_DATE,
  content: {
    eyebrow: 'BotSpot trading guides',
    heading: 'Build, test, connect, and operate trading strategies',
    summary:
      'Follow focused guides for choosing a workflow, turning rules into inspectable code, testing historical evidence, controlling deployment risk, and connecting ChatGPT, Claude, or Codex to supported broker workflows.',
    primaryAction: { label: 'Start building in BotSpot', url: '/ai-agent' },
    secondaryAction: { label: 'Compare trading platforms', url: '/compare' },
    proofPoints: agentProofPoints,
    sections: [
      {
        heading: 'Choose a guide by the decision you need to make',
        paragraphs: [
          'Start with platform selection when choosing tools. Use build guides to formalize strategy rules, backtesting guides to inspect evidence, and deployment guides to validate broker and operational controls.',
        ],
      },
      {
        heading: 'Treat every backtest as evidence with limits',
        paragraphs: [
          'Historical results depend on data, timing, costs, fills, parameter choices, and software behavior. Use the linked guides to inspect those assumptions instead of treating one performance number as proof.',
        ],
      },
      {
        heading: 'Keep broker access and high-risk actions controlled',
        paragraphs: [
          'Broker support, assets, account permissions, paper or live modes, and authentication methods vary. Connect credentials through BotSpot, keep secrets out of prompts, and review high-risk actions through the required approval flow.',
        ],
      },
    ],
    cards: guideDetailPages.map((route) => ({
      eyebrow: route.content.eyebrow,
      heading: route.content.heading,
      summary: route.description,
      url: route.path,
    })),
    relatedLinks: [
      { label: 'Compare AI trading platforms', url: '/compare' },
      { label: 'Explore supported brokers', url: '/brokers' },
      { label: 'MCP for Agents', url: '/agents' },
    ],
    sources: [
      { label: 'BotSpot MCP for Agents', url: '/agents' },
      { label: 'BotSpot broker connection manifest', url: '/.well-known/botspot-manifest.json' },
      { label: 'BotSpot Terms of Service', url: '/terms' },
      { label: 'Lumibot documentation', url: 'https://lumibot.lumiwealth.com/' },
    ],
    verifiedAt: SEO_EXPANSION_VERIFIED_DATE,
  },
};

function formatConnectionModes(broker) {
  return broker.modes
    .map((mode) => (mode === 'paper' ? 'paper trading' : 'live trading'))
    .join(' and ');
}

function formatAuthMethods(broker) {
  return broker.authMethods
    .map((method) => (method === 'api_key' ? 'API key' : 'OAuth'))
    .join(' and ');
}

function formatNaturalList(items) {
  if (items.length < 2) return items[0] || '';
  if (items.length === 2) return items.join(' and ');
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
}

function brokerPageFor(broker) {
  const assetSummary = formatNaturalList(broker.assetClasses);
  const connectPath = buildBrokerConnectPath(broker.id);
  const warningParagraphs = broker.warning ? [broker.warning] : [];

  return {
    path: `/brokers/${broker.id}`,
    title: `Use BotSpot with ${broker.name} | AI Trading Broker Connection`,
    description: `Connect ${broker.name} to BotSpot for AI research, supported approved trading actions, strategy building, backtesting, and ${assetSummary.toLowerCase()} workflows.`,
    pageType: 'article',
    lastmod: '2026-10-02',
    modifiedAt: '2026-10-02',
    content: {
      eyebrow: 'Supported BotSpot broker connection',
      heading: `Use BotSpot with ${broker.name}`,
      summary: `Connect ${broker.name} to the BotSpot AI trading workspace for conversational research, supported user-approved trading actions, and complete automated strategy workflows across ${assetSummary}.`,
      primaryAction: { label: `Connect ${broker.name}`, url: connectPath },
      secondaryAction: { label: 'Explore every supported broker', url: '/brokers' },
      proofPoints: agentProofPoints,
      sections: [
        {
          heading: `What BotSpot adds to ${broker.name}`,
          paragraphs: [
            `${broker.name} supplies the connected account and eligible order execution. BotSpot adds the conversational agent, research workflow, explicit trade approvals, strategy code, backtests, revisions, and operations layer.`,
          ],
          bullets: [
            'Research an idea before choosing whether to trade it directly or automate it.',
            'Prepare a one-time order for review and approval when the connected account, asset, and BotSpot action support it.',
            'Build, revise, backtest, and operate a complete Lumibot algorithm.',
            'Use BotSpot directly or work through compatible clients such as ChatGPT, Claude, Cursor, and Codex.',
          ],
        },
        {
          heading: `${broker.name} connection details`,
          paragraphs: [
            `Current BotSpot connection data lists ${assetSummary} with ${formatConnectionModes(broker)}. Available connection methods are ${formatAuthMethods(broker)}.`,
            ...warningParagraphs,
            'Broker products, permissions, market data, fees, regional eligibility, and order support can change. Confirm current requirements with the broker before connecting or trading.',
          ],
          bullets: broker.authMethods.map((method) => {
            const modes = broker.modesByAuthMethod?.[method] || broker.modes;
            const label = method === 'api_key' ? 'API key' : 'OAuth';
            const modeText = modes
              .map((mode) => (mode === 'paper' ? 'paper trading' : 'live trading'))
              .join(' and ');
            return `${label}: ${modeText}.`;
          }),
        },
        {
          heading: `Connect and inspect your ${broker.name} account`,
          paragraphs: [
            `Use the Connect ${broker.name} link to open the broker-specific setup in BotSpot. Authenticate using one of the listed methods and verify the resulting account and mode in Broker Connections. Connection success alone does not establish order permissions, market-data access or live readiness.`,
          ],
          bullets: [
            'Check that the connected broker, account and trading mode match your intended workflow.',
            'Confirm asset eligibility, account approvals, data subscriptions and broker fees in the broker’s own documentation.',
            'Keep credentials in the protected connection flow, never in prompts or public strategy code.',
            'If a connection or action is unavailable, inspect the visible connection status and broker requirements before retrying. A connected account does not bypass approval of an order or deployment.',
          ],
        },
        {
          heading: 'Trading safety',
          paragraphs: [
            'A broker connection does not authorize BotSpot to trade without your controls. One-time high-risk actions require explicit review and approval. Automated strategies require separate deployment decisions and remain exposed to market and execution risk.',
          ],
        },
      ],
      faq: [
        {
          question: `Can BotSpot connect to ${broker.name}?`,
          answer: `Yes. ${broker.name} is listed in the current BotSpot public broker connection source. Current BotSpot modes are ${formatConnectionModes(broker)}.`,
        },
        {
          question: `What can the BotSpot agent do with ${broker.name}?`,
          answer:
            'The agent can research, help prepare one-time trading actions for explicit approval when direct trading is enabled, and build or operate strategy workflows. Exact capabilities depend on the connected account, asset, broker permissions, and current BotSpot tool support.',
        },
      ],
      relatedLinks: [
        { label: 'MCP for Agents', url: '/agents' },
        { label: 'View every supported broker', url: '/brokers' },
        { label: 'Review BotSpot pricing', url: '/pricing' },
      ],
      sources: [
        { label: 'BotSpot broker connection manifest', url: '/.well-known/botspot-manifest.json' },
        { label: `${broker.name} documentation`, url: broker.docsUrl },
        { label: 'Lumibot documentation', url: 'https://lumibot.lumiwealth.com/' },
      ].filter((item) => item.url),
      verifiedAt: SOURCE_VERIFIED_DATE,
    },
  };
}

const brokerDetailPages = ALL_BROKER_CONNECTION_SOURCE.map(brokerPageFor);

const brokerHubPage = {
  path: '/brokers',
  title: 'BotSpot Broker Connections | AI Trading with Your Broker',
  description:
    'Explore supported BotSpot broker connections for stocks, options, futures, and crypto, with AI research, approved trades, and strategy automation.',
  pageType: 'article',
  lastmod: SOURCE_VERIFIED_DATE,
  content: {
    eyebrow: 'Supported broker connections',
    heading: 'Bring BotSpot’s AI agent to your broker',
    summary:
      'Your broker holds the account and executes eligible orders. BotSpot supplies the conversational research, explicit approval workflow, strategy building, backtesting, and operations layer.',
    primaryAction: {
      label: 'Connect a broker',
      url: '/account-settings/broker-connections?action=add',
    },
    secondaryAction: { label: 'See MCP setup', url: '/agents' },
    proofPoints: agentProofPoints,
    sections: [
      {
        heading: 'One AI workflow across supported accounts',
        paragraphs: [
          'Choose a broker page to see current asset classes, paper or live modes, authentication methods, and broker-specific notices. These pages are generated from the same source used by the BotSpot connection interface and public manifest.',
        ],
      },
    ],
    cards: ALL_BROKER_CONNECTION_SOURCE.map((broker) => ({
      eyebrow: broker.assetClasses.join(' • '),
      heading: `Use BotSpot with ${broker.name}`,
      summary: `${formatConnectionModes(broker)} through ${formatAuthMethods(broker)}.${broker.warning ? ` ${broker.warning}` : ''}`,
      url: `/brokers/${broker.id}`,
    })),
    relatedLinks: [
      { label: 'MCP for Agents', url: '/agents' },
      { label: 'Compare BotSpot and QuantConnect', url: '/compare/botspot-vs-quantconnect' },
      { label: 'Read the platform buyer guide', url: '/guides/automated-trading-platforms' },
    ],
    sources: [
      { label: 'BotSpot broker connection manifest', url: '/.well-known/botspot-manifest.json' },
      { label: 'Lumibot documentation', url: 'https://lumibot.lumiwealth.com/' },
    ],
    verifiedAt: SOURCE_VERIFIED_DATE,
  },
};

const publicPositioningPages = [
  guideHubPage,
  ...comparisonPages,
  brokerHubPage,
  ...brokerDetailPages,
]
  .map(withGuideWorkflowExample)
  .map((route) => ({
    ...route,
    author: BOTSPOT_EDITORIAL_AUTHOR,
  }));

const comparisonByPath = new Map(publicPositioningPages.map((page) => [page.path, page]));

function getComparisonPage(pathname) {
  return comparisonByPath.get(pathname) || null;
}

module.exports = {
  SOURCE_VERIFIED_DATE,
  brokerDetailPages,
  brokerHubPage,
  comparisonPages,
  guideHubPage,
  getComparisonPage,
  publicPositioningPages,
};
